import axios from 'axios'

const util = require('../../util.js')

const default_filter = {
  id: null,
  site: null,
  name: null,
  source: process.env.VUE_APP_DEFAULT_PRODUCT_SOURCE,
  productForm: null,
  productType: null,
  componentType: null,
  licenseType: null,
  includeBundledProducts: Boolean(false)
}

const default_paging = {
  currentPage: 1,
  perPage: 25,
  perPageAlternatives: [25, 50, 100],
  totalRows: 0
}

const default_product = {
  source: undefined,
  sourceId: undefined,
  ean: undefined,
  productType: undefined,
  forSale: undefined,
  name: undefined,
  site: undefined,
  licenseType: undefined,
  licenceDuration: undefined,
  price: undefined,
  includeInInvoice: undefined,
  forSaleFrom: undefined,
  accessibleFrom: undefined,
  bundledProducts: undefined,
  bundlePrice: undefined,
  created: undefined,
  modified: undefined,
}

const default_create_local_product = {
  ean: undefined,
  name: undefined,
  productType: undefined,
  licenseType: undefined
}

const state = {
  product: { ...default_product },
  products: [],
  filter: { ...default_filter },
  paging: { ...default_paging },
  loading: false,
  events: [],
  isShowEventButtonEnabled: true,
  bundledProducts: [],
  parentProducts: [],
  localProduct: { ...default_create_local_product }
}

const mutations = {
  UPDATE_PRODUCTS (state, payload) {
    state.products = payload.response
    state.paging.totalRows = payload.resultCount
  },
  UPDATE_PRODUCT (state, payload) {
    state.product = payload
  },
  UPDATE_FILTER (state, payload) {
    state.filter = payload
  },
  UPDATE_PAGING (state, payload) {
    state.paging = payload
  },
  UPDATE_EVENTS (state, payload) {
    state.events = payload
    state.isShowEventButtonEnabled = false
  },
  UPDATE_EMPTY_EVENT (state) {
    state.events = null
    state.isShowEventButtonEnabled = true
  },
  UPDATE_FORM_DISABLE_INDICATOR (state, payload) {
    state.loading = payload
  },
  UPDATE_BUNDLE_PRODUCT (state, payload) {
    state.bundledProducts = payload
  },
  ADD_TO_BUNDLED_PRODUCT (state, payload) {
    state.bundledProducts.push(payload)
  },
  UPDATE_PARENT_PRODUCTS (state, payload) {
    state.parentProducts = payload
  },
  UPDATE_LOCAL_PRODUCT (state, payload) {
    state.localProduct = payload
  },
  UPDATE_LOCAL_PRODUCT_CREATE_REQUEST (state, payload) {
    state.localProduct.ean = payload.ean
    state.localProduct.name = payload.name
    state.localProduct.productType = payload.productType
    state.localProduct.licenseType = payload.licenseType
  },
  RESET_CREATE_STATE (state, payload) {
    state.localProduct = payload
  },
  UPDATE_EAN_SOURCE (state, payload) {
    state.filter.id = payload.ean
    state.filter.source = payload.source
  }
}

const actions = {
  getProducts ({ commit }) {
    commit('UPDATE_FORM_DISABLE_INDICATOR', true)
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/', {
        params: {
          id: util.emptyToNull(state.filter.id),
          site: util.emptyToNull(state.filter.site),
          name: util.emptyToNull(state.filter.name),
          source: util.emptyToNull(state.filter.source),
          productForm: util.emptyToNull(state.filter.productForm),
          productType: util.emptyToNull(state.filter.productType),
          componentType: util.emptyToNull(state.filter.componentType),
          licenseType: util.emptyToNull(state.filter.licenseType),
          includeBundledProducts: state.filter.includeBundledProducts ? 'true' : null,
          pageNumber: util.emptyToNull(state.paging.currentPage),
          pageSize: util.emptyToNull(state.paging.perPage)
        }
      }).then((response) => {
      commit('UPDATE_PRODUCTS', response.data)
    }).finally(() => {
      commit('UPDATE_FORM_DISABLE_INDICATOR', false)
    })
  },
  async getProductBySourceId ({ commit, dispatch }, params) {
    const product = await axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/' + params.source + '/SOURCE_ID/' + params.sourceId, {
        params: {}
      })

    await dispatch('getMetadata', product.data.ean, params.preferredLanguage)

    if (product.data.isBundle === true) {
      let bundleProduct = await axios
        .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/' + product.data.source + '/SOURCE_ID/' + product.data.sourceId + '/bundle')
      if (bundleProduct.data) {
        commit('UPDATE_BUNDLE_PRODUCT', bundleProduct.data)
      }
    } else {
      commit('UPDATE_BUNDLE_PRODUCT', [])
    }
    commit('UPDATE_PRODUCT', product.data)
  },
  createOrUpdateProductBySourceId ({ commit }, sourceId) {
    // Set bundled products to null to indicate we have no wish for updating the products
    state.product.bundledProducts = null
    axios.put(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/' + state.product.source + '/SOURCE_ID/' + sourceId, state.product)
      .then((response) => {
        commit('UPDATE_PRODUCT', response.data)
      })
  },
  resetFilter ({ commit }) {
    commit('UPDATE_PAGING', { ...default_paging })
    commit('UPDATE_FILTER', { ...default_filter })
    state.products = []
  },
  resetProduct ({ commit }) {
    commit('UPDATE_PRODUCT', { ...default_product })
  },
  getObjectReference ({ dispatch, commit }, objectReference) {
    return dispatch('events/getObjectReference', { objectReference: objectReference }, { root: true })
      .then((response) => {
          if (response.length > 0) {
            commit('UPDATE_EVENTS', response)
          } else {
            commit('UPDATE_EMPTY_EVENT')
          }
        }
      )
  },
  async getProduct (state, { id }) {
    const product = await axios.get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/', {
      params: {
        source: process.env.VUE_APP_DEFAULT_PRODUCT_SOURCE,
        id: id
      }
    })
    return product.data.response
  },
  removeBundledProduct ({ commit }, sourceId) {
    let updatedBundledProducts = state.bundledProducts.filter(item => item.sourceId !== sourceId)
    commit('UPDATE_BUNDLE_PRODUCT', updatedBundledProducts)
  },
  updateBundledProducts ({ commit }) {
    axios
      .put(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/' + state.product.source + '/SOURCE_ID/' + state.product.sourceId + '/bundle', state.bundledProducts)
      .then((response) => {
        commit('UPDATE_BUNDLE_PRODUCT', response.data)
      })
  },
  addBundledProduct ({ commit }, product) {
    let bundledProductExists = state.bundledProducts.some(item => item.sourceId === product.sourceId)
    if (!bundledProductExists) {
      commit('ADD_TO_BUNDLED_PRODUCT', product)
    }
  },
  getParentProducts ({ commit }, params) {
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/' + params.source + '/SOURCE_ID/' + params.sourceId + '/parents')
      .then((response) => {
        commit('UPDATE_PARENT_PRODUCTS', response.data)
      })
  },
  getMetadata ({ dispatch }, ean, preferredLanguage) {
    return dispatch('metadata/getMetadata', { ean: ean, preferredLanguage: preferredLanguage }, { root: true })
      .then(metadataResponse => metadataResponse)
  },
  async createOrUpdateLocalProduct ({ commit }) {
    commit('UPDATE_FORM_DISABLE_INDICATOR', true)
    await axios.put(process.env.VUE_APP_INTERNAL_API_BASEURL + '/product/LOCAL/EAN/' + state.localProduct.ean, state.localProduct)
      .then((response) => {
        commit('UPDATE_LOCAL_PRODUCT', response.data)
      }).finally(() => {
        commit('UPDATE_FORM_DISABLE_INDICATOR', false)
      })
  },
  populateLocalCreateRequest ({ commit }, params) {
    commit('UPDATE_LOCAL_PRODUCT_CREATE_REQUEST', params)
  },
  resetCreateLocalForm ({ commit }) {
    commit('RESET_CREATE_STATE', { ...default_create_local_product })
  },
  updateEanAndSource ({ commit }, params) {
    commit('UPDATE_EAN_SOURCE', params)
  }
}

const getters = {
  filter: state => state.filter,
  product: state => state.product,
  products: state => state.products,
  paging: state => state.paging,
  events: state => state.events,
  isShowEventButtonEnabled: state => state.isShowEventButtonEnabled,
  loading: state => state.loading,
  bundledProducts: state => state.bundledProducts,
  parentProducts: state => state.parentProducts,
  localProduct: state => state.localProduct
}

const productsModule = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export default productsModule
